import React, { useRef } from 'react'
import { ReactComponent as CheckCircleIcon } from 'assets/icons/check-circle.svg'
import { ReactComponent as QuestionCircleIcon } from 'assets/icons/question-circle.svg'
import { ReactComponent as UploadIcon } from 'assets/icons/upload.svg'
import { ReactComponent as BellIcon } from 'assets/icons/bell.svg'
import { ReactComponent as ReloadIcon } from 'assets/icons/reload-2.svg'
import { AppDetailIcon } from '../../../icons/AppDetailIcon'
import ListRow from '../ListRow/ListRow'
import RowIconHintButton from '../ListRow/RowIconHintButton'
import { DOCUMENT_NUDGE_COOLDOWN_TOOLTIP } from '../../../utils/documentNudgeCooldown'
import { fmtDateTime } from '../../../utils/applicationDetailsFormat'
import list from '../ListRow/ListRow.module.scss'
import s from './DocumentChecklistRow.module.scss'

const formatLastUpdatedMeta = iso => {
	if (!iso) return null
	const label = fmtDateTime(iso)
	return label === '—' ? null : label
}

function DocumentChecklistRow({
	item,
	onAssign,
	onUploadClick,
	onNudge,
	onReupload,
	onView,
	nudgeOnCooldown = false,
	isUploading = false,
	isNew = false,
	className = '',
}) {
	const inputRef = useRef(null)
	const isSuccess = item.counselorStatus === 'success'
	const isAssigned = item.counselorStatus === 'assigned'
	const metaLabel = isSuccess ? formatLastUpdatedMeta(item.lastUpdated) : null

	const statusIcon = isSuccess ? (
		<CheckCircleIcon className={s.statusOk} aria-hidden />
	) : (
		<QuestionCircleIcon className={s.statusPending} aria-hidden />
	)

	const trailing = isSuccess ? (
		<div className={s.actions}>
			<RowIconHintButton
				icon={<ReloadIcon />}
				hint="Re-upload"
				ariaLabel="Re-upload document"
				onClick={() => onReupload?.(item)}
			/>
			<button
				type="button"
				className={[list.action, list.static].join(' ')}
				onClick={event => {
					event.stopPropagation()
					onView?.(item)
				}}
			>
				View
			</button>
		</div>
	) : isAssigned ? (
		<div className={s.actions}>
			<RowIconHintButton
				className={s.bellBtn}
				icon={<BellIcon />}
				hint="Remind student"
				cooldownHint={DOCUMENT_NUDGE_COOLDOWN_TOOLTIP}
				disabled={nudgeOnCooldown}
				ariaLabel={
					nudgeOnCooldown ? 'Reminder on cooldown' : 'Remind student'
				}
				onClick={() => onNudge?.(item)}
			/>
			<span className={s.assignedBadge}>
				<span className={s.assignedBadgeMark} aria-hidden>
					✓
				</span>
				Assigned
			</span>
		</div>
	) : (
		<div className={s.actions}>
			<input
				ref={inputRef}
				type="file"
				className={s.hiddenInput}
				accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
				onChange={event => {
					const file = event.target.files?.[0]
					event.target.value = ''
					if (file) onUploadClick?.(item, file)
				}}
			/>
			<RowIconHintButton
				className={s.iconUpload}
				icon={<UploadIcon />}
				hint="Upload yourself"
				ariaLabel="Upload document"
				disabled={isUploading}
				onClick={() => inputRef.current?.click()}
			/>
			<button
				type="button"
				className={s.assignBtn}
				onClick={event => {
					event.stopPropagation()
					onAssign?.(item)
				}}
			>
				Assign
			</button>
		</div>
	)

	return (
		<ListRow
			className={[
				list['row--docs'],
				isUploading ? list['row--loading'] : '',
				className,
			]
				.filter(Boolean)
				.join(' ')}
			leading={statusIcon}
			title={item.title}
			titleBadge={isNew ? <span className={s.newPill}>New</span> : null}
			subtitle={metaLabel || null}
			subtitleIcon={
				metaLabel ? (
					<AppDetailIcon
						name="schedule"
						size={14}
						className={list.subtitleIcon}
					/>
				) : null
			}
			trailing={trailing}
		/>
	)
}

export default DocumentChecklistRow
